import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Telescope, LogOut, Zap, Menu, X, User, Code, Info } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useTopup } from '../contexts/TopupContext';
import { useUserPlan } from '../services/plan';

const Header: React.FC = () => {
  const { isAuthenticated, user, displayName, signOut, signIn } = useAuth();
  const { open: openTopup, canTopup } = useTopup();
  const nav = useNavigate();
  const loc = useLocation();

  // Balance chip shares the single ['profile'] TanStack query with
  // useUserPlan on Dashboard / PanalyzerModal / etc. — one /profile
  // per tab per staleTime window instead of one per consumer. Failure
  // is non-fatal; the chip stays hidden.
  const plan = useUserPlan();
  const balance = plan.loading || plan.error ? null : plan.creditsBalance;
  const isAdmin = !plan.loading && !plan.error && plan.isUnlimited;

  // Mobile menu open/close. Auto-close on route change so tapping a link
  // in the drawer doesn't leave it hanging over the destination page.
  const [menuOpen, setMenuOpen] = React.useState(false);
  React.useEffect(() => { setMenuOpen(false); }, [loc.pathname]);
  // Lock body scroll + close on Escape while the mobile drawer is open —
  // it's a floating overlay now, so the content behind it should feel
  // "frozen" the way a modal would.
  React.useEffect(() => {
    if (!menuOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);
  const reduceMotion = useReducedMotion();

  // Desktop avatar dropdown. Same auto-close on route change; also closes
  // on outside-click (see effect below).
  const [userMenuOpen, setUserMenuOpen] = React.useState(false);
  React.useEffect(() => { setUserMenuOpen(false); }, [loc.pathname]);
  const userMenuRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!userMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [userMenuOpen]);

  const avatarInitial = (displayName()[0] || '?').toUpperCase();
  const avatarUrl = user?.picture as string | undefined;

  // Topup wiring is admin-only during the feature-flag window (see
  // TopupContext). Non-admins still see the balance chip — just as a
  // read-only status pill without the click-to-topup affordance.
  const balanceChipClasses = `whitespace-nowrap inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${
    balance !== null && balance < 0
      ? 'border-red-400/40 bg-red-500/10 text-red-200'
      : balance !== null && balance < 300
      ? 'border-amber-400/40 bg-amber-500/10 text-amber-200'
      : 'border-white/10 bg-white/[0.04] text-white/75'
  }`;
  // Admin variant: swap the counted-balance chip for a gold infinity so
  // it's obvious at a glance the caller is unlimited (also matches the
  // ∞ credits treatment in CreditsMeter). Gold hex is a warm amber-yellow
  // that reads distinctly from the amber "low balance" warning tone.
  const adminChipClasses =
    'whitespace-nowrap inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ' +
    'border-yellow-300/50 bg-gradient-to-r from-yellow-500/15 via-amber-400/20 to-yellow-500/15 ' +
    'text-yellow-200 shadow-[0_0_18px_rgba(250,204,21,0.25)] transition-colors';
  const adminChip = (
    <span
      title="Admin plan — unlimited credits"
      className={adminChipClasses}
    >
      <Zap className="h-3.5 w-3.5 text-yellow-300 drop-shadow-[0_0_4px_rgba(250,204,21,0.7)]" />
      <span
        className="text-lg leading-none text-yellow-200 drop-shadow-[0_0_6px_rgba(250,204,21,0.6)]"
        aria-label="unlimited"
      >
        ∞
      </span>
    </span>
  );
  const countedChip = balance !== null && (
    canTopup ? (
      <button
        type="button"
        onClick={openTopup}
        title={`${balance.toLocaleString()} stacking credits — click to add more`}
        className={`${balanceChipClasses} cursor-pointer hover:bg-white/[0.08]`}
      >
        <Zap className="h-3.5 w-3.5" />
        <span className="tabular-nums">{balance.toLocaleString()}</span>
        <span className="opacity-60 hidden sm:inline">credits</span>
      </button>
    ) : (
      <span
        title={`${balance.toLocaleString()} stacking credits`}
        className={balanceChipClasses}
      >
        <Zap className="h-3.5 w-3.5" />
        <span className="tabular-nums">{balance.toLocaleString()}</span>
        <span className="opacity-60 hidden sm:inline">credits</span>
      </span>
    )
  );
  const balanceChip = isAdmin ? adminChip : countedChip;

  return (
    <header className="relative z-30 py-4 md:py-5">
      {/* Match the main content container (max-w-6xl + px-6/md:px-10, see
          Dashboard.tsx <main>) so the logo and profile chip align with
          the content edges instead of pinning to the viewport. */}
      <div className="relative mx-auto max-w-6xl px-6 md:px-10">
      <div className="flex items-center justify-between">
        <Link to={isAuthenticated ? '/dashboard' : '/'} className="flex items-center gap-2 group">
          <div className="relative">
            <Telescope className="h-6 w-6 text-nebula-300 group-hover:text-nebula-200 transition-colors" />
            <div className="absolute inset-0 blur-xl bg-nebula-500/40 -z-10" />
          </div>
          <span className="font-display text-lg font-semibold tracking-tight">
            Pancake<span className="text-nebula-300">Stack</span>
          </span>
        </Link>

        {/* Desktop nav — hidden below md, replaced by the hamburger. */}
        <nav className="hidden md:flex items-center gap-4">
          {isAuthenticated && (
            <button
              onClick={() => nav('/collections')}
              className="whitespace-nowrap text-sm text-white/70 hover:text-white transition-colors px-3 py-1.5"
            >
              Collections
            </button>
          )}
          <Link
            to="/try"
            className="whitespace-nowrap text-sm text-white/70 hover:text-white transition-colors px-3 py-1.5"
          >
            FITS-preview
          </Link>
          <a
            href="/sky/"
            className="whitespace-nowrap text-sm text-white/70 hover:text-white transition-colors px-3 py-1.5"
          >
            Sky
          </a>
          <Link
            to="/docs"
            className="whitespace-nowrap text-sm text-white/70 hover:text-white transition-colors px-3 py-1.5"
          >
            Docs
          </Link>
          {isAuthenticated ? (
            <>
              {balanceChip}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(v => !v)}
                  aria-haspopup="menu"
                  aria-expanded={userMenuOpen}
                  aria-label="Account menu"
                  className="flex items-center gap-2 rounded-full p-0.5 pr-2 hover:bg-white/5 transition-colors border border-transparent hover:border-white/10"
                >
                  <Avatar url={avatarUrl} initial={avatarInitial} />
                  <span className="hidden lg:inline whitespace-nowrap text-sm text-white/70 max-w-[10rem] truncate">
                    {displayName()}
                  </span>
                </button>
                {userMenuOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 mt-2 w-56 rounded-xl border border-white/10 bg-black/80 backdrop-blur shadow-2xl overflow-hidden z-40"
                  >
                    <div className="px-4 py-3 border-b border-white/10">
                      <div className="text-sm text-white truncate">{displayName()}</div>
                      {user?.email && (
                        <div className="text-xs text-white/50 truncate mt-0.5">
                          {user.email as string}
                        </div>
                      )}
                    </div>
                    <div className="py-1">
                      <MenuItem icon={<User className="h-4 w-4" />} onClick={() => { setUserMenuOpen(false); nav('/profile'); }}>
                        Profile
                      </MenuItem>
                      <MenuItem icon={<Code className="h-4 w-4" />} onClick={() => { setUserMenuOpen(false); nav('/api-docs'); }}>
                        API
                      </MenuItem>
                      <MenuItem icon={<Info className="h-4 w-4" />} onClick={() => { setUserMenuOpen(false); nav('/about'); }}>
                        About
                      </MenuItem>
                    </div>
                    <div className="border-t border-white/10 py-1">
                      <MenuItem icon={<LogOut className="h-4 w-4" />} onClick={() => { setUserMenuOpen(false); signOut(); }}>
                        Sign out
                      </MenuItem>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link
                to="/api-docs"
                className="whitespace-nowrap text-sm text-white/70 hover:text-white transition-colors px-3 py-1.5"
              >
                API
              </Link>
              <Link
                to="/about"
                className="whitespace-nowrap text-sm text-white/70 hover:text-white transition-colors px-3 py-1.5"
              >
                About
              </Link>
              <button
                onClick={signIn}
                className="whitespace-nowrap px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur text-sm font-medium transition-colors border border-white/10"
              >
                Sign in
              </button>
            </>
          )}
        </nav>

        {/* Mobile controls — balance chip stays visible so the user can
            see their minutes without opening the drawer; everything else
            collapses behind the hamburger. */}
        <div className="flex md:hidden items-center gap-2">
          {isAuthenticated && balanceChip}
          <button
            onClick={() => setMenuOpen(v => !v)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-white/10 bg-white/5 text-white/80 hover:bg-white/10"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer — floating overlay, anchored under the hamburger.
          Scrim behind at z-20 (sits under header's z-30 so the hamburger
          stays tappable to close); panel at z-40 above the header. Scroll
          lock + Escape close via the effect above. Dinesh verdict: don't
          slide in from the left when the trigger is top-right (Jakob's
          Law) — overlay-anchor-under-trigger matches iOS/Twitter/Gmail. */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            key="mobile-menu-scrim"
            className="md:hidden fixed inset-0 z-20 bg-black/50 backdrop-blur-sm"
            onClick={() => setMenuOpen(false)}
            aria-hidden
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            key="mobile-menu-panel"
            className="md:hidden absolute top-full right-6 mt-2 w-72 max-w-[calc(100vw-3rem)] rounded-xl border border-white/10 bg-black/80 backdrop-blur-xl shadow-2xl p-2 flex flex-col z-40"
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -8, scale: 0.98 }}
            animate={reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
          >
            {isAuthenticated ? (
              <>
                <button
                  onClick={() => nav('/profile')}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-md text-left hover:bg-white/5"
                >
                  <Avatar url={avatarUrl} initial={avatarInitial} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-white truncate">{displayName()}</div>
                    {user?.email && (
                      <div className="text-xs text-white/50 truncate">
                        {user.email as string}
                      </div>
                    )}
                  </div>
                </button>
                <div className="h-px bg-white/10 my-1" />
                <MobileItem onClick={() => nav('/collections')}>Collections</MobileItem>
                <MobileItem onClick={() => nav('/try')}>FITS-preview</MobileItem>
                <MobileItem onClick={() => { window.location.href = '/sky/'; }}>Sky</MobileItem>
                <MobileItem onClick={() => nav('/docs')}>Docs</MobileItem>
                <div className="h-px bg-white/10 my-1" />
                <MobileItem onClick={() => nav('/api-docs')}>API</MobileItem>
                <MobileItem onClick={() => nav('/about')}>About</MobileItem>
                <div className="h-px bg-white/10 my-1" />
                <MobileItem onClick={signOut}>
                  <span className="inline-flex items-center gap-2">
                    <LogOut className="h-4 w-4" /> Sign out
                  </span>
                </MobileItem>
              </>
            ) : (
              <>
                <MobileItem onClick={() => nav('/try')}>FITS-preview</MobileItem>
                <MobileItem onClick={() => { window.location.href = '/sky/'; }}>Sky</MobileItem>
                <MobileItem onClick={() => nav('/docs')}>Docs</MobileItem>
                <MobileItem onClick={() => nav('/api-docs')}>API</MobileItem>
                <MobileItem onClick={() => nav('/about')}>About</MobileItem>
                <div className="h-px bg-white/10 my-1" />
                <MobileItem onClick={signIn}>Sign in</MobileItem>
              </>
            )}
          </motion.nav>
        )}
      </AnimatePresence>
      </div>
    </header>
  );
};

const MobileItem: React.FC<React.PropsWithChildren<{ onClick: () => void }>> = ({ onClick, children }) => (
  <button
    onClick={onClick}
    className="text-left text-sm text-white/80 hover:text-white hover:bg-white/5 rounded-md px-3 py-2.5"
  >
    {children}
  </button>
);

const Avatar: React.FC<{ url?: string; initial: string }> = ({ url, initial }) => {
  const [failed, setFailed] = React.useState(false);
  const showImg = url && !failed;
  return (
    <div className="h-8 w-8 rounded-full overflow-hidden bg-gradient-to-br from-nebula-500 to-aurora-500 flex items-center justify-center shrink-0">
      {showImg ? (
        <img
          src={url}
          alt=""
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <span className="text-white text-xs font-semibold">{initial}</span>
      )}
    </div>
  );
};

const MenuItem: React.FC<React.PropsWithChildren<{ icon: React.ReactNode; onClick: () => void }>> = ({
  icon,
  onClick,
  children,
}) => (
  <button
    role="menuitem"
    onClick={onClick}
    className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors"
  >
    <span className="text-white/60">{icon}</span>
    {children}
  </button>
);

export default Header;
